import { useEffect, useState, lazy, Suspense } from 'react';
import { getSavedPlaces, removeSaved, directionsUrl } from '../services/savedPlacesService';
import { getEnrichmentForPlaces } from '../services/enrichmentService';
import { track } from '../services/firebase';
import { getSourceAttribute } from '../utils/placeAttributes';

const POICard = lazy(() => import('./POICard'));

const TYPE_EMOJI = {
  park: '🌲', hike: '🥾', trail: '🚶', water: '💧', waterfall: '💧',
  viewpoint: '🏔️', swimming: '🏊', fishing: '🎣', picnic: '🧺',
  'boat-launch': '🛥️', marina: '⚓', tennis: '🎾', basketball: '🏀', soccer: '⚽',
};

const CHIP = {
  permit_or_fee: { none: 'Free', fee: 'Day-use fee', permit: 'Permit needed', license: 'License needed' },
  parking: { yes: 'Parking', limited: 'Limited parking', no: 'No parking' },
  dog_friendly: { yes: 'Dogs OK', leashed: 'Dogs on leash', no: 'No dogs' },
  difficulty: { easy: 'Easy', moderate: 'Moderate', hard: 'Hard' },
};

export default function SavedPlacesView({ onClose }) {
  const [places, setPlaces] = useState(() => getSavedPlaces());
  const [enrichment, setEnrichment] = useState(null);
  const [openPlace, setOpenPlace] = useState(null);

  useEffect(() => {
    track('saved_places_opened', { count: places.length });
    const refresh = () => setPlaces(getSavedPlaces());
    window.addEventListener('go:saved-places-changed', refresh);
    return () => window.removeEventListener('go:saved-places-changed', refresh);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Saved places carry only what we stored; pull their verified answers back.
  useEffect(() => {
    if (!places.length) return undefined;
    let alive = true;
    getEnrichmentForPlaces(places).then((m) => alive && setEnrichment(m));
    return () => {
      alive = false;
    };
  }, [places]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-white">
      <div className="sticky top-0 z-10 border-b border-stone-100 bg-white/95 px-4 py-3 backdrop-blur-md">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <button
            onClick={onClose}
            aria-label="Back"
            className="-ml-1 rounded-full p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-base font-semibold text-[#2d3b2d]">Places I want to go</h1>
            <p className="text-[11px] text-stone-400">
              {places.length ? `${places.length} saved` : 'Nothing saved yet'}
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-lg px-4 py-4">
        {places.length === 0 && (
          <div className="rounded-2xl border border-dashed border-stone-200 px-4 py-10 text-center">
            <p className="text-3xl">🔖</p>
            <p className="mt-3 text-sm font-medium text-[#2d3b2d]">Your shortlist lives here</p>
            <p className="mx-auto mt-1 max-w-xs text-xs leading-relaxed text-stone-500">
              Tap <span className="font-medium">Save</span> on any place and it&rsquo;ll wait for you
              here, with any place notes we have collected.
            </p>
            <button onClick={onClose} className="mt-4 text-sm font-medium text-[#2d3b2d] hover:underline">
              Find somewhere to go
            </button>
          </div>
        )}

        <div className="space-y-2">
          {places.map((place) => {
            const rec = enrichment?.get(place.id);
            const chips = [];
            for (const [key, labels] of Object.entries(CHIP)) {
              const v = String(getSourceAttribute(rec, key)?.value ?? '').toLowerCase();
              if (labels[v]) chips.push(labels[v]);
              if (chips.length >= 3) break;
            }
            return (
              <div
                key={place.id}
                className="flex items-start gap-3 rounded-2xl border border-stone-100 bg-white p-3 shadow-[0_1px_2px_rgba(28,38,28,0.04)]"
              >
                <span className="mt-0.5 text-xl leading-none">{TYPE_EMOJI[place.type] ?? '📍'}</span>
                <button onClick={() => setOpenPlace(place)} className="min-w-0 flex-1 text-left">
                  <h3 className="truncate text-sm font-semibold text-[#2d3b2d]">{place.name}</h3>
                  <p className="text-[11px] text-stone-400">
                    {place.designation ?? place.type}
                    {place.distanceWhenSaved != null && ` · ${place.distanceWhenSaved.toFixed(1)} mi when saved`}
                  </p>
                  {chips.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {chips.map((c) => (
                        <span key={c} className="rounded-full bg-stone-100 px-1.5 py-0.5 text-[10px] text-stone-500">
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
                <div className="flex shrink-0 items-center gap-1">
                  <a
                    href={directionsUrl(place)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Directions to ${place.name}`}
                    className="rounded-lg p-2 text-stone-400 transition-colors hover:bg-stone-100 hover:text-[#2d3b2d]"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </a>
                  <button
                    onClick={() => {
                      removeSaved(place.id);
                      setPlaces(getSavedPlaces());
                    }}
                    aria-label={`Remove ${place.name}`}
                    className="rounded-lg p-2 text-stone-300 transition-colors hover:bg-stone-100 hover:text-stone-500"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {openPlace && (
        <div className="fixed inset-0 z-[60]">
          <button
            onClick={() => setOpenPlace(null)}
            className="absolute inset-0 bg-black/35"
            aria-label="Close place details"
          />
          <Suspense fallback={null}>
            <POICard poi={openPlace} onClose={() => setOpenPlace(null)} />
          </Suspense>
        </div>
      )}
    </div>
  );
}
